import { useRef, useState } from 'react';
import { notifications } from '@mantine/notifications';
import type { AttendanceDashboardView } from 'shared-models/browser';
import type { EmployeeIdentity, EmployeeView } from '../../../../services/employees/employees.service';
import type { PrinterView } from '../../../../services/printers/printers.service';
import { attendanceService } from '../../../../services/attendance/attendance.service';
import { toHebrewApiErrorMessage } from '../../../../services/index/index.service';

export type AttendanceBatchReport = {
  employee: EmployeeView | EmployeeIdentity;
  dashboard: AttendanceDashboardView;
  range: { from: string; to: string };
};

export function useAttendanceBatchPrint({ tenantId, employeeSession, employees, range, enabled, renderImage, clearReport }: {
  tenantId: string;
  employeeSession: string;
  employees: Array<EmployeeView | EmployeeIdentity>;
  range: { from: string; to: string };
  enabled: boolean;
  renderImage: (report: AttendanceBatchReport) => Promise<string | undefined>;
  clearReport: () => void;
}) {
  const inFlight = useRef(false);
  const [pending, setPending] = useState<{ employees: typeof employees; range: typeof range } | null>(null);
  const [confirmationOpen, setConfirmationOpen] = useState(false);
  const [printers, setPrinters] = useState<PrinterView[] | null>(null);
  const [progress, setProgress] = useState<{ current: number; completed: number; total: number } | null>(null);
  const [completedTotal, setCompletedTotal] = useState<number | null>(null);

  const begin = (employeeIds: ReadonlyArray<number>) => {
    if (!enabled || inFlight.current || completedTotal !== null) return;
    const selected = employees.filter((employee) => employeeIds.includes(employee.id));
    if (selected.length === 0) return;
    setPending({ employees: selected, range: { ...range } });
    setConfirmationOpen(true);
  };

  const cancel = () => {
    if (inFlight.current) return;
    setPending(null);
    setConfirmationOpen(false);
    setPrinters(null);
  };

  const print = async (printerId?: number) => {
    if (!enabled || !pending || inFlight.current) return;
    inFlight.current = true;
    setConfirmationOpen(false);
    const total = pending.employees.length;
    let completed = 0;
    let currentEmployee = pending.employees[0];
    setProgress({ current: 1, completed, total });
    try {
      let target = printerId;
      if (target === undefined) {
        const available = await attendanceService.listBillablePrinters(tenantId);
        if (available.length === 0) throw new Error('אין מדפסות חשבונות פעילות');
        if (available.length > 1) {
          setPrinters(available);
          return;
        }
        target = available[0]!.id;
      } else if (!printers?.some((printer) => printer.id === target)) {
        throw new Error('בחר מדפסת מתוך הרשימה');
      }
      setPrinters(null);
      for (const employee of pending.employees) {
        currentEmployee = employee;
        setProgress({ current: completed + 1, completed, total });
        const dashboard = await attendanceService.getDashboard(tenantId, employeeSession, { ...pending.range, employeeId: employee.id });
        const image = await renderImage({ employee, dashboard, range: pending.range });
        if (!image) throw new Error('לא ניתן ליצור תמונה להדפסה');
        await attendanceService.printSlip(tenantId, target, image);
        completed += 1;
        setProgress({ current: completed, completed, total });
      }
      setCompletedTotal(total);
      setPending(null);
    } catch (error: unknown) {
      notifications.show({ color: 'red', title: 'ההדפסה נכשלה', message: `${currentEmployee?.fullName ?? ''}: ${completed}/${total} דוחות נשלחו. ${error instanceof Error ? toHebrewApiErrorMessage(error.message) : 'שגיאה לא ידועה'}` });
      setPending(null);
      setPrinters(null);
    } finally {
      clearReport();
      setProgress(null);
      inFlight.current = false;
    }
  };

  return { begin, cancel, print, confirmationOpen, printers, progress, completedTotal,
    acknowledge: () => setCompletedTotal(null), busy: progress !== null || completedTotal !== null };
}